import { useEffect, useState } from "react";
import { Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { PasswordInput } from "@/components/PasswordInput";
import { PurchaseCard, ExtensionOption, startCheckout, gbp } from "@/components/PurchaseCard";
import { useAuth } from "@/context/AuthContext";
import { IfGifts } from "@/hooks/useGiftsEnabled";
import { api, apiError } from "@/lib/api";

const MISMATCH = "Your passwords don't match. Please try again.";
const POLICY = "Please choose a password with at least 8 characters, including at least one letter and one number.";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function checkSignup(f) {
  if (!f.name.trim()) return "Please enter your name.";
  if (!EMAIL_RE.test(f.email.trim())) return "Please enter a valid email address.";
  if (f.password.length < 8 || !/[A-Za-z]/.test(f.password) || !/\d/.test(f.password)) return POLICY;
  if (f.password !== f.confirm) return MISMATCH;
  return "";
}
export const field = "w-full h-14 px-4 rounded-xl bg-[#1A1222] border border-[#33264A] text-[#EDE6F7] text-lg placeholder:text-[#8C84A0] focus:outline-none focus:border-[#8B2CFF] focus:ring-2 focus:ring-[rgba(139,44,255,0.35)] transition-colors";

export function Notice({ tone = "info", children, testid }) {
  const Icon = tone === "error" ? AlertCircle : tone === "note" ? Info : CheckCircle2;
  const cls = tone === "error" ? "border-[#6B2A4A] bg-[#1E1428] text-[#E6CCF2]" : "border-[rgba(139,44,255,0.4)] bg-[#1E1428] text-[#D9C6FF]";
  return (
    <div role={tone === "error" ? "alert" : "status"} data-testid={testid} className={`flex gap-3 rounded-xl border px-4 py-3 text-base ${cls}`}>
      <Icon className="h-5 w-5 mt-0.5 shrink-0" aria-hidden="true" /> <span>{children}</span>
    </div>
  );
}

function AccountForm({ extension }) {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "signup");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setError(""); };
  const signup = mode === "signup";

  const submit = async (e) => {
    e.preventDefault();
    const problem = signup ? checkSignup(form) : (!EMAIL_RE.test(form.email.trim()) || !form.password ? "Please enter your email address and password." : "");
    if (problem) return setError(problem);
    setLoading(true);
    setError("");
    try {
      const payload = { email: form.email.trim(), password: form.password };
      const { data } = await api.post(signup ? "/auth/register" : "/auth/login",
        signup ? { ...payload, name: form.name.trim(), confirm_password: form.confirm } : payload);
      if (data.mfa) return navigate("/two-step", { state: { challenge: data.challenge, mode: data.mfa } });
      login(data.token, data.user);
      if (data.user.paid) return navigate("/dashboard");
      if (signup) await startCheckout(extension ? ["base", "extension"] : ["base"]);
    } catch (err) {
      setError(apiError(err));
      setLoading(false);
    }
  };

  const tab = (m, label) => (
    <button type="button" role="tab" aria-selected={mode === m} data-testid={`auth-tab-${m}`}
      onClick={() => { setMode(m); setError(""); }}
      className={`flex-1 h-12 rounded-full text-base font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B2CFF] ${mode === m ? "gold-btn" : "text-[#C9C2D8] hover:text-[#EDE6F7]"}`}>
      {label}
    </button>
  );

  return (
    <div className="glass-card p-7 sm:p-9 fade-up" data-testid="account-form-card">
      <div role="tablist" aria-label="Account options" className="flex gap-1 p-1 rounded-full bg-[#1A1222] border border-[#2E2140] mb-8">
        {tab("signup", "Create account")}
        {tab("login", "Log in")}
      </div>
      <h1 className="font-serif text-3xl sm:text-4xl text-[#EDE6F7] mb-2" data-testid="auth-heading">
        {signup ? "Create your account" : "Welcome back"}
      </h1>
      <p className="text-[#B9B3C8] mb-7 text-lg">
        {signup ? "Start keeping the voices you love." : "Log in to continue with your family's memories."}
      </p>
      {params.get("reset") === "1" && !error && (
        <div className="mb-5"><Notice testid="auth-reset-success">Your password has been updated. Please log in.</Notice></div>
      )}
      <form onSubmit={submit} className="space-y-5" noValidate={false}>
        {signup && (
          <div>
            <label htmlFor="auth-name" className="eyebrow block mb-2">Name</label>
            <input id="auth-name" name="name" data-testid="auth-name" className={field} value={form.name} onChange={set("name")} required autoComplete="name" placeholder="Your full name" />
          </div>
        )}
        <div>
          <label htmlFor="auth-email" className="eyebrow block mb-2">Email address</label>
          <input id="auth-email" name="email" data-testid="auth-email" type="email" inputMode="email" className={field} value={form.email} onChange={set("email")} required autoComplete={signup ? "email" : "username"} placeholder="you@example.com" />
        </div>
        <PasswordInput label="Password" name="password" testid="auth-password" value={form.password} onChange={set("password")} autoComplete={signup ? "new-password" : "current-password"} error={!!error} />
        {signup && <p className="-mt-3 text-sm text-[#8C84A0]">At least 8 characters, including a letter and a number.</p>}
        {signup && <PasswordInput label="Confirm password" name="confirm_password" testid="auth-confirm" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" error={error === MISMATCH} describedBy={error ? "auth-error" : undefined} />}
        {error && <div id="auth-error"><Notice tone="error" testid="auth-error">{error}</Notice></div>}
        <button data-testid="auth-submit" type="submit" disabled={loading}
          className="gold-btn w-full min-h-16 py-3 px-6 rounded-full text-lg sm:text-xl disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EDE6F7] focus-visible:ring-offset-2 focus-visible:ring-offset-[#120B19]">
          {loading ? "Please wait…" : signup ? "Create account & continue to payment" : "Log in"}
        </button>
        {signup && <p className="text-center text-[#C9C2D8]" data-testid="auth-total">You'll pay {gbp(extension ? 1498 : 999)} on the next step.</p>}
      </form>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-base">
        <Link to="/forgot-password" data-testid="forgot-password-link" className="gold-text font-semibold underline underline-offset-4">Forgot password?</Link>
        <IfGifts><Link to="/redeem" data-testid="auth-redeem-link" className="text-[#C9C2D8] underline underline-offset-4 hover:text-[#EDE6F7]">Have a gift code?</Link></IfGifts>
        <button type="button" data-testid="auth-toggle" onClick={() => { setMode(signup ? "login" : "signup"); setError(""); }} className="text-[#C9C2D8] underline underline-offset-4 hover:text-[#EDE6F7]">
          {signup ? "Already have an account? Log in" : "New here? Create an account"}
        </button>
      </div>
    </div>
  );
}

function ResumePanel({ extension, onExtension }) {
  const { user, logout } = useAuth();
  const [last, setLast] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/payments/latest").then((r) => setLast(r.data)).catch(() => {}); }, []);
  const go = async () => {
    setLoading(true); setError("");
    try { await startCheckout(extension ? ["base", "extension"] : ["base"]); } catch (e) { setError(apiError(e)); setLoading(false); }
  };
  const msg = { failed: "Your last payment didn't go through. You haven't been charged.",
    expired: "Your last checkout timed out before payment. You haven't been charged.",
    processing: "Your payment is still being confirmed by your bank. We'll unlock your account as soon as it's confirmed.",
    pending: "Your purchase isn't complete yet." }[last?.state] || "Your purchase isn't complete yet.";
  return (
    <div className="glass-card p-7 sm:p-9 fade-up" data-testid="resume-panel">
      <p className="eyebrow mb-2">Your account</p>
      <h1 className="font-serif text-3xl sm:text-4xl text-[#EDE6F7] mb-3">Complete your purchase</h1>
      <p className="text-lg text-[#C9C2D8] mb-6">You're logged in as <span className="text-[#EDE6F7]" data-testid="resume-email">{user.email}</span>.</p>
      <Notice testid="resume-status">{msg} Your account is saved — there's no need to create another.</Notice>
      <div className="mt-5"><ExtensionOption checked={extension} onChange={onExtension} testid="resume-extension-option" /></div>
      {error && <div className="mt-4"><Notice tone="error" testid="resume-error">{error}</Notice></div>}
      <button type="button" data-testid="resume-checkout-btn" onClick={go} disabled={loading || last?.state === "processing"}
        className="gold-btn mt-7 w-full min-h-16 py-3 rounded-full text-lg sm:text-xl disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EDE6F7]">
        {loading ? "Opening secure checkout…" : `Continue to secure payment · ${gbp(extension ? 1498 : 999)}`}
      </button>
      <button type="button" data-testid="resume-logout-btn" onClick={logout} className="mt-5 w-full text-center text-[#C9C2D8] underline underline-offset-4 hover:text-[#EDE6F7]">
        Log out
      </button>
      <IfGifts><Link to="/redeem" data-testid="resume-redeem-link" className="mt-3 block text-center text-[#B57BFF] underline underline-offset-4">Have a gift code? Unlock with it instead</Link></IfGifts>
    </div>
  );
}

export default function Auth() {
  const { user, ready } = useAuth();
  const [extension, setExtension] = useState(false);
  if (!ready) return <div className="min-h-screen legacy-bg" />;
  if (user && user.paid) return <Navigate to="/dashboard" replace />;
  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">
        {user ? <ResumePanel extension={extension} onExtension={setExtension} /> : <AccountForm extension={extension} />}
        <PurchaseCard extension={extension} onExtension={setExtension} />
      </main>
    </div>
  );
}
